import type { ComponentProps, ReactNode } from "react";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { BoardCardAvatarField } from "@design-system/Board/BoardCardAvatarField";
import { BoardCardTag } from "@design-system/Board/BoardCardTag";
import { CalendarIcon } from "@icons";
import { useAppliedByMember } from "../cells/useAppliedByMember";
import { formatWorkDate } from "../cells/WorkCloseDateCell";
import {
    clientsWorkOverviewViewConfig,
    type ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { getWorkOverdueLabel } from "../workOverdue";
import { getWorkFinishedStatus } from "./workBoardStatus";

/** A List column's name, as the card's tags and the +N list name it. */
export const getWorkCardFieldName = (id: ClientsWorkOverviewColumn) =>
    clientsWorkOverviewViewConfig.columns[id].name;

interface WorkCardTagProps extends Omit<
    ComponentProps<typeof BoardCardTag>,
    "field"
> {
    /** The List column the tag shows, which names its field. */
    id: ClientsWorkOverviewColumn;
}

/** A read-only card tag for a List column, named after the column. */
export const WorkCardTag = ({ id, ...props }: Readonly<WorkCardTagProps>) => (
    <BoardCardTag field={getWorkCardFieldName(id)} {...props} />
);

/** A tag whose value doesn't say which field it is ("0 / 7", "Manual"): the column's name shows on hover. */
export const workCardFieldNameTag = (
    id: ClientsWorkOverviewColumn,
    content: ReactNode,
) => (
    <WorkCardTag id={id} tooltip="fieldName">
        {content}
    </WorkCardTag>
);

/** A date tag, named after its column on hover since the date alone doesn't say which. */
export const workCardDateTag = (
    id: ClientsWorkOverviewColumn,
    content: string,
) => (
    <WorkCardTag id={id} tooltip="fieldName" icon={<CalendarIcon />}>
        {content}
    </WorkCardTag>
);

/** Work end; late open work says how late on hover. */
export const WorkCardDeliveryDate = ({
    row,
}: Readonly<{
    row: Pick<
        ClientsWorkOverviewWorkRow,
        "isFinished" | "cancelledAt" | "workDeliveryDate"
    >;
}>) => {
    if (!row.workDeliveryDate) {
        return null;
    }
    const overdueLabel = getWorkOverdueLabel(row);
    return (
        <WorkCardTag
            id={ClientsWorkOverviewColumn.WORK_DELIVERY_DATE}
            tooltip="fieldName"
            detail={overdueLabel ?? undefined}
            icon={<CalendarIcon />}
        >
            {formatWorkDate(row.workDeliveryDate)}
        </WorkCardTag>
    );
};

/** Applied by as a standalone colorless avatar, `Applied by · Name` on hover; nothing until members load. */
export const WorkCardAppliedBy = ({ userId }: Readonly<{ userId: number }>) => {
    const appliedBy = useAppliedByMember(userId);
    if (!appliedBy) {
        return null;
    }
    const id = ClientsWorkOverviewColumn.APPLIED_BY;
    const { member, name } = appliedBy;
    if (!member) {
        return workCardFieldNameTag(id, name);
    }
    return (
        <BoardCardAvatarField field={getWorkCardFieldName(id)} value={name}>
            <UserAvatar user={member.user} size="sm" />
        </BoardCardAvatarField>
    );
};

/** The stage tag: Done or Canceled (with its multi-tone icon in its own colors) for finished work, else the stage's name. */
export const getWorkCardStageTag = (
    row: Pick<
        ClientsWorkOverviewWorkRow,
        "cancelledAt" | "isFinished" | "closeStageName"
    >,
) => {
    const id = ClientsWorkOverviewColumn.CLOSE_STAGE;
    const status = getWorkFinishedStatus(row);
    if (status) {
        return (
            <WorkCardTag
                id={id}
                tooltip="fieldName"
                icon={<status.Icon className="icon-color-revert" aria-hidden />}
            >
                {status.label}
            </WorkCardTag>
        );
    }
    return row.closeStageName && workCardFieldNameTag(id, row.closeStageName);
};
